import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Check, AlertTriangle, Eye, EyeOff, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function JoinInvitePage() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/team/invites/${token}`)
      .then(async r => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Invalid invite');
        setInvite(d);
      })
      .catch(e => setInvalid(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    setError('');
    if (!name.trim() || !email.trim()) { setError('Name and email are required'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/team/invites/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Could not create your account');
      setDone(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" data-testid="join-invite-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center">
              <Users size={22} />
            </div>
            <div>
              <h1 className="text-xl font-bold">Join the team</h1>
              <p className="text-sm text-emerald-100">StreakFree</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-zinc-500">
              <Loader2 size={20} className="animate-spin" /> Checking your invite…
            </div>
          ) : invalid ? (
            <div className="py-6 text-center" data-testid="join-invite-invalid">
              <AlertTriangle size={36} className="mx-auto text-amber-500 mb-3" />
              <p className="font-semibold text-zinc-800">{invalid}</p>
              <p className="text-sm text-zinc-500 mt-2">Ask the business owner to send you a new invite link.</p>
            </div>
          ) : done ? (
            <div className="py-6 text-center" data-testid="join-invite-success">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <Check size={28} className="text-emerald-600" />
              </div>
              <p className="font-semibold text-zinc-800">Your account is ready</p>
              <p className="text-sm text-zinc-500 mt-1">Sign in with {email} and the password you just set.</p>
              <a
                href="/"
                className="mt-5 inline-block px-6 py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600"
                data-testid="join-invite-login-link"
              >
                Go to sign in
              </a>
            </div>
          ) : (
            <>
              <p className="text-sm text-zinc-600 mb-5" data-testid="join-invite-info">
                <span className="font-semibold text-zinc-800">{invite.company_name}</span> has invited you to join
                as a <span className="font-semibold text-emerald-700">{invite.role}</span>. Set up your login below.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-zinc-700 mb-1">Your name</label>
                  <input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Full name"
                    className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="join-invite-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-zinc-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="join-invite-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-zinc-700 mb-1">Choose a password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Min 6 characters"
                      className="w-full px-4 py-3 pr-11 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      data-testid="join-invite-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                      data-testid="join-invite-toggle-password"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700" data-testid="join-invite-error">
                    {error}
                  </div>
                )}
                <button
                  onClick={submit}
                  disabled={saving}
                  className="w-full py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="join-invite-submit"
                >
                  {saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                  Create my account
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
